import { BillingStrategy } from '@ctct/domain-layer';

import { AddOnsService } from './services/AddOnsService';
import type { AddonsUrlAdapter, Init } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type AddOnsData = MergedDataReturnType<[AddOnsService]>;

export type AddOnsHandlers = MergedHandlersReturnType<[AddOnsService]>;

type TApi = ReturnType<AddOnsService['api']>;

export class AddOnsManager extends WithApi<TApi> {
  constructor(private urlAdapter: AddonsUrlAdapter) {
    super();
  }

  public addOnsInit = ({
    pageStore,
    queries,
    t,
    billingStrategy,
    isSmsBundle,
    isValueFocusedMWebPlanSelectionEnabled,
    isSingleBillChildAccount,
    isSingleBillChildAccountPending,
  }: Init) => {
    const [billingAccountQuery, addOnsQuery, paymentPlanQuery, planDetailQuery] = queries;

    const pending =
      billingAccountQuery.isPending ||
      addOnsQuery.isPending ||
      paymentPlanQuery.isPending ||
      planDetailQuery.isPending ||
      Boolean(isSingleBillChildAccountPending);
    const error = billingAccountQuery.error || addOnsQuery.error || paymentPlanQuery.error || planDetailQuery.error;

    const addOns = new AddOnsService(
      pageStore,
      billingAccountQuery.data,
      addOnsQuery.data,
      paymentPlanQuery.data,
      planDetailQuery.data,
      t,
      this.urlAdapter,
      billingStrategy === BillingStrategy.Stripe,
      isSmsBundle ?? false,
      isValueFocusedMWebPlanSelectionEnabled ?? false,
      isSingleBillChildAccount
    );

    this.initApi({
      ...addOns.api(),
    });

    return {
      status: this.getStatus(pending, !!error),
      data: {
        ...addOns.data(),
        error,
      },
      handlers: {
        ...addOns.handlers(),
        retry: () => {
          managerHelpers.retryQueries(queries);
        },
      },
      lifecycle: {
        onMount() {
          addOns.onMount();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };

  private getStatus(isPending: boolean, isError: boolean) {
    if (isPending) return 'pending';

    if (isError) return 'error';

    return 'loaded';
  }
}
